import type { Module } from 'vuex';
import { GlobalCurrency } from '@/types/GlobalCurrency';
import { GlobalCurrencyCurrency } from '@/types/GlobalCurrencyCurrency';
import {RootState} from "@/store";

export interface GlobalCurrenciesState {
  globalCurrencies: GlobalCurrency[];
}

const globalCurrenciesModule: Module<GlobalCurrenciesState, RootState> = {
  namespaced: true,

  state: (): GlobalCurrenciesState => ({
    globalCurrencies: [],
  }),

  getters: {
    getById: (state) => (idGlobalCurrency: number): GlobalCurrency | undefined => {
      return state.globalCurrencies.find(globalCurrency => globalCurrency.idGlobalCurrency === idGlobalCurrency);
    },
  },

  mutations: {
    setGlobalCurrencies(
      state,
      payload: { globalCurrencies: Record<number, any> }
    ) {
      state.globalCurrencies = Object.values(payload.globalCurrencies).map(raw => {
        let globalCurrencyCurrencies: any[] = [];
        if (raw.currencies && typeof raw.currencies === 'object') {
          globalCurrencyCurrencies = Object.values(raw.currencies).map((alt: any) => ({
            idGlobalCurrency: alt.idGlobalCurrency,
            idCurrency: alt.idCurrency,
          }));
        } else if (Array.isArray(raw.globalCurrencyCurrencies)) {
          globalCurrencyCurrencies = raw.globalCurrencyCurrencies;
        }
        return GlobalCurrency.fromRaw({
          ...raw,
          globalCurrencyCurrencies,
        });
      });
    },

    setGlobalCurrencyCreate(
      state,
      payload: {
        idGlobalCurrency: number;
        idCurrency: number;
        locked: boolean;
        globalCurrencyCurrencies?: any[];
      }
    ) {
      const raw = {
        idGlobalCurrency: payload.idGlobalCurrency,
        idCurrency: payload.idCurrency,
        locked: payload.locked,
        globalCurrencyCurrencies: Array.isArray(payload.globalCurrencyCurrencies) ? payload.globalCurrencyCurrencies : [],
      };
      state.globalCurrencies.push(GlobalCurrency.fromRaw(raw));
    },

    setGlobalCurrencyUpdate(
      state,
      payload: {
        idGlobalCurrency: number;
        idCurrency?: number;
        locked?: boolean;
      }
    ) {
      const globalCurrency = state.globalCurrencies.find(globalCurrency => globalCurrency.idGlobalCurrency === payload.idGlobalCurrency);
      if (!globalCurrency) return;
      if (payload.idCurrency !== undefined) globalCurrency.idCurrency = payload.idCurrency;
      if (payload.locked !== undefined) globalCurrency.locked = payload.locked;
    },

    setGlobalCurrencyDelete(
      state,
      payload: { idGlobalCurrency: number }
    ) {
      state.globalCurrencies = state.globalCurrencies.filter(
        globalCurrency => globalCurrency.idGlobalCurrency !== payload.idGlobalCurrency
      );
    },

    setGlobalCurrencyLock(
      state,
      payload: { idGlobalCurrency: number }
    ) {
      const globalCurrency = state.globalCurrencies.find(globalCurrency => globalCurrency.idGlobalCurrency === payload.idGlobalCurrency);
      if (globalCurrency) globalCurrency.locked = true;
    },

    setGlobalCurrencyUnlock(
      state,
      payload: { idGlobalCurrency: number }
    ) {
      const globalCurrency = state.globalCurrencies.find(globalCurrency => globalCurrency.idGlobalCurrency === payload.idGlobalCurrency);
      if (globalCurrency) globalCurrency.locked = false;
    },

    setGlobalCurrencyCurrencyAdd(
      state,
      payload: {
        idGlobalCurrency: number;
        idCurrency: number;
      }
    ) {
      const globalCurrency = state.globalCurrencies.find(globalCurrency => globalCurrency.idGlobalCurrency === payload.idGlobalCurrency);
      if (globalCurrency) {
        globalCurrency.globalCurrencyCurrencies.push(
          GlobalCurrencyCurrency.fromRaw({
            idGlobalCurrency: payload.idGlobalCurrency,
            idCurrency: payload.idCurrency,
          })
        );
      }
    },

    setGlobalCurrencyCurrencyDelete(
      state,
      payload: {
        idGlobalCurrency: number;
        idCurrency: number;
      }
    ) {
      const globalCurrency = state.globalCurrencies.find(globalCurrency => globalCurrency.idGlobalCurrency === payload.idGlobalCurrency);
      if (globalCurrency) {
        globalCurrency.globalCurrencyCurrencies = globalCurrency.globalCurrencyCurrencies.filter(
          globalCurrencyCurrency => globalCurrencyCurrency.idCurrency !== payload.idCurrency
        );
      }
    },
  },

  actions: {
    wsSetGlobalCurrencies({ commit }, payload) {
      commit('setGlobalCurrencies', payload);
    },
    wsGlobalCurrencyCreate({ commit }, payload) {
      commit('setGlobalCurrencyCreate', payload);
    },
    wsGlobalCurrencyUpdate({ commit }, payload) {
      commit('setGlobalCurrencyUpdate', payload);
    },
    wsGlobalCurrencyDelete({ commit }, payload) {
      commit('setGlobalCurrencyDelete', payload);
    },
    wsGlobalCurrencyLock({ commit }, payload) {
      commit('setGlobalCurrencyLock', payload);
    },
    wsGlobalCurrencyUnlock({ commit }, payload) {
      commit('setGlobalCurrencyUnlock', payload);
    },
    wsGlobalCurrencyCurrencyAdd({ commit }, payload) {
      commit('setGlobalCurrencyCurrencyAdd', payload);
    },
    wsGlobalCurrencyCurrencyDelete({ commit }, payload) {
      commit('setGlobalCurrencyCurrencyDelete', payload);
    },
  },
};

export default globalCurrenciesModule; 